import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, Loader2, Lock, Sparkles } from "lucide-react";
import {
  createTrade,
  formatApiError,
  getQuote,
  listPortfolios,
  toastApiError,
} from "@/lib/api";
import { fmtQty, fmtUsd, isCryptoSymbol } from "@/lib/format";

/**
 * Verdict → paper-trade panel. Rendered inside the analyze verdict card
 * whenever a completed run has an ACTIONABLE rating. Never silently hidden
 * for SELL/UNDERWEIGHT — if no portfolio qualifies, we render a note.
 */
export default function VerdictTrade({ run, onTraded }) {
  const [portfolios, setPortfolios] = useState(null); // null = loading, [] = loaded empty
  const [pid, setPid] = useState("");
  const [qtyStr, setQtyStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [price, setPrice] = useState(null); // null = loading, 0 = quote failed
  const [priceErr, setPriceErr] = useState(null);

  const symbol = run?.symbol;
  const verdict = run?.verdict;
  const rating = verdict?.rating;

  // Which ratings surface the trade panel.
  const side = useMemo(() => {
    if (!rating) return null;
    if (rating === "BUY" || rating === "OVERWEIGHT") return "BUY";
    if (rating === "SELL" || rating === "UNDERWEIGHT") return "SELL";
    return null; // HOLD is intentionally silent
  }, [rating]);

  useEffect(() => {
    if (!side) return;
    let cancelled = false;
    // Fetch portfolios and quote INDEPENDENTLY. A quote failure (invalid symbol,
    // yfinance blip) must never mislabel the user as having no portfolios.
    (async () => {
      try {
        const pl = await listPortfolios();
        if (!cancelled) setPortfolios(pl);
      } catch (e) {
        if (cancelled) return;
        setPortfolios([]);
        toastApiError(e);
      }
    })();
    (async () => {
      try {
        const q = await getQuote(symbol);
        if (!cancelled) {
          setPrice(Number(q?.price) || 0);
          setPriceErr(null);
        }
      } catch (e) {
        if (cancelled) return;
        setPrice(0);
        setPriceErr(formatApiError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [side, symbol]);

  // Portfolios that would qualify as a "target" for this trade.
  //   BUY: any portfolio (cash constraint checked at submit)
  //   SELL: only portfolios that actually hold this symbol.
  const eligible = useMemo(() => {
    if (!portfolios) return [];
    if (side === "BUY") return portfolios;
    return portfolios.filter((p) =>
      (p.positions || []).some((pos) => pos.symbol === symbol),
    );
  }, [portfolios, side, symbol]);

  // Auto-select the first eligible portfolio so the CTA always has a target.
  useEffect(() => {
    if (!pid && eligible.length > 0) setPid(eligible[0].id);
  }, [eligible, pid]);

  const selected = useMemo(
    () => portfolios?.find((p) => p.id === pid) || null,
    [portfolios, pid],
  );

  const heldQty = useMemo(() => {
    if (!selected || !symbol) return 0;
    const pos = (selected.positions || []).find((p) => p.symbol === symbol);
    return pos ? Number(pos.quantity) : 0;
  }, [selected, symbol]);

  const suggestedQty = useMemo(() => {
    if (!selected || !verdict || !price) return 0;
    if (side === "SELL") return heldQty;
    const tgtPct = Number(verdict.target_allocation_pct) || 0;
    const dollars = (tgtPct / 100) * Number(selected.total_equity || selected.cash || 0);
    const q = dollars / price;
    return Number.isFinite(q) && q > 0 ? q : 0;
  }, [selected, verdict, side, price, heldQty]);

  useEffect(() => {
    if (!suggestedQty) return;
    setQtyStr(
      isCryptoSymbol(symbol) ? suggestedQty.toFixed(8) : suggestedQty.toFixed(4),
    );
  }, [suggestedQty, symbol]);

  if (!side) return null;

  // ------------- No-op states (still render the CTA container) -------------
  //
  // Design intent per Phase-2 spec: NEVER hide the CTA on actionable verdicts.
  // If there is a legitimate reason we cannot trade, render an explanatory
  // note in the same panel so the user is not left staring at empty space.

  const loading = portfolios === null;
  const noPortfoliosAtAll = !loading && portfolios.length === 0;
  const noQualifyingPortfolio =
    !loading && !noPortfoliosAtAll && side === "SELL" && eligible.length === 0;
  const noLivePrice = price === 0 && !!priceErr;

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const qty = Number(qtyStr) || 0;
      const res = await createTrade(pid, {
        symbol,
        side,
        quantity: qty,
        source: "verdict",
        analysis_run_id: run.run_id,
      });
      toast.success(
        `${side} ${fmtQty(qty, symbol)} ${symbol} · cash after ${fmtUsd(res.cash_after)}`,
      );
      setQtyStr("");
      if (onTraded) onTraded();
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const qty = Number(qtyStr) || 0;
  const estUsd = qty * (price || 0);
  const cashAvailable = selected ? Number(selected.cash) : 0;
  const autoModeOn = !!selected?.desk_settings?.auto_mode;

  const canSubmit =
    !!pid &&
    !autoModeOn &&
    qty > 0 &&
    estUsd >= 1 &&
    (side === "SELL"
      ? qty <= heldQty + 1e-9
      : estUsd <= cashAvailable + 0.01);

  // Inline hint mirrors the trade ticket's UX so a disabled button is never
  // silent.
  let disabledHint = null;
  if (autoModeOn) {
    disabledHint =
      `Autopilot is ON for ${selected?.name || "this portfolio"} — pause Autopilot to trade manually.`;
  } else if (!loading && selected && qty > 0 && price) {
    if (estUsd < 1) {
      disabledHint = `Trade value ${fmtUsd(estUsd)} below $1.00 minimum`;
    } else if (side === "BUY" && estUsd > cashAvailable + 0.01) {
      const maxQty = cashAvailable / price;
      disabledHint = `Insufficient cash in ${selected.name} — max ${fmtQty(maxQty, symbol)} ${symbol}`;
    } else if (side === "SELL" && qty > heldQty + 1e-9) {
      disabledHint = `You only hold ${fmtQty(heldQty, symbol)} ${symbol} in ${selected.name}`;
    }
  } else if (!loading && selected && !price && priceErr) {
    // Portfolios loaded fine but the quote failed — surface it instead of a
    // silent grey button.
    disabledHint = `No live price for ${symbol} — cannot size or execute this trade (${priceErr})`;
  }

  return (
    <Card
      data-testid="verdict-trade-cta"
      className="mt-6 border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/5 p-5"
    >
      {/* legacy id retained for backwards-compat testids */}
      <div data-testid="verdict-trade-panel" className="sr-only" aria-hidden />
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--aurion-gold)]">
        <Sparkles className="h-3.5 w-3.5" />
        Paper-trade this verdict
        <span className="ml-2 rounded-none border border-[var(--aurion-gold)]/40 px-1.5 py-0.5 text-[10px] tracking-widest">
          {side} {symbol}
        </span>
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[var(--aurion-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your portfolios…
        </div>
      ) : noPortfoliosAtAll ? (
        <Note testId="verdict-trade-note">
          You don't have any portfolios yet.{" "}
          <Link
            to="/portfolios"
            data-testid="verdict-trade-goto-portfolios"
            className="text-[var(--aurion-gold)] underline"
          >
            Create one
          </Link>{" "}
          to paper-trade this verdict.
        </Note>
      ) : noQualifyingPortfolio ? (
        <Note testId="verdict-trade-note">
          None of your portfolios hold {symbol} — there's no position to sell
          against this {rating} verdict. Paper-buy it first from{" "}
          <Link
            to="/portfolios"
            data-testid="verdict-trade-goto-portfolios"
            className="text-[var(--aurion-gold)] underline"
          >
            a portfolio
          </Link>{" "}
          if you want to test the thesis.
        </Note>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Portfolio
              </Label>
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger
                  data-testid="verdict-portfolio-select"
                  className="mt-1 h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
                >
                  <SelectValue placeholder="Select portfolio…" />
                </SelectTrigger>
                <SelectContent className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]">
                  {eligible.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      data-testid={`verdict-portfolio-option-${p.id}`}
                    >
                      {p.name} · {fmtUsd(p.total_equity)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Quantity (PM target · {verdict.target_allocation_pct}%)
              </Label>
              <Input
                data-testid="verdict-qty-input"
                type="number"
                step={isCryptoSymbol(symbol) ? "0.00000001" : "0.0001"}
                min="0"
                value={qtyStr}
                onChange={(e) => setQtyStr(e.target.value)}
                className="mt-1 h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
              />
              {selected ? (
                <div className="mt-1 text-[10px] text-[var(--aurion-muted)]">
                  {side === "SELL" ? (
                    <>held: {fmtQty(heldQty, symbol)} {symbol}</>
                  ) : (
                    <>
                      cash: {fmtUsd(selected.cash)} · est {fmtUsd(estUsd)}
                    </>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex items-end">
              <Button
                data-testid="verdict-trade-submit"
                onClick={submit}
                disabled={busy || !canSubmit}
                className={`h-10 w-full border font-semibold tracking-widest ${
                  autoModeOn
                    ? "border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)]"
                    : side === "BUY"
                    ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                    : "border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25"
                }`}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {autoModeOn ? (
                  <span
                    data-testid="verdict-trade-autopilot-lock"
                    className="flex items-center justify-center gap-1"
                  >
                    <Lock className="h-3.5 w-3.5" />
                    Autopilot ON
                  </span>
                ) : (
                  <>
                    {side} {symbol}
                  </>
                )}
              </Button>
            </div>
          </div>

          {disabledHint ? (
            <div data-testid="verdict-trade-hint" className="mt-3 text-xs text-amber-300">
              {disabledHint}
            </div>
          ) : null}
          {err ? (
            <div data-testid="verdict-trade-error" className="mt-3 text-sm text-red-400">
              {err}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function Note({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="mt-4 flex items-start gap-2 text-sm text-[var(--aurion-muted)]"
    >
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--aurion-muted)]" />
      <p>{children}</p>
    </div>
  );
}
